import { useEffect, useRef, useState } from 'react';
import {
  GoogleAuthProvider,
  RecaptchaVerifier,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPhoneNumber,
  signInWithPopup,
  type ConfirmationResult,
} from 'firebase/auth';
import { auth, missingConfig, usingEmulators } from '../firebase';
import { Icon } from '../ui/Icon';
import { Mark } from '../ui/Mark';
import { useStore } from '../app/store';
import { accountWords, session, signOut, type Gate } from './session';

/** Firebase's errors in plain words, as the phone says them (auth_service.dart `emailErrorMessage`). */
function words(code: string): string {
  switch (code) {
    case 'auth/invalid-email':
      return 'That email doesn’t look right.';
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Wrong email or password.';
    case 'auth/user-disabled':
      return 'This account has been turned off.';
    case 'auth/too-many-requests':
      return 'Too many tries. Wait a minute and try again.';
    case 'auth/network-request-failed':
      return 'No connection. Check your internet and try again.';
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return '';
    case 'auth/popup-blocked':
      return 'The browser blocked Google’s window. Allow pop-ups for this site and try again.';
    case 'auth/invalid-phone-number':
      return 'That number doesn’t look right. Start it with + and the country code.';
    case 'auth/invalid-verification-code':
      return 'That code isn’t right. Check the text and try again.';
    case 'auth/code-expired':
      return 'That code has run out. Ask for a new one.';
    case 'auth/unauthorized-domain':
      return 'This address isn’t allowed to sign in yet (Firebase Auth’s authorised domains).';
    default:
      return 'Something went wrong. Please try again.';
  }
}

const codeOf = (e: unknown) => (e as { code?: string })?.code ?? '';

/** A number as typed, with +234 in front when it has no + (Nigeria, where Routr starts); null when it can't be one. */
export function phoneE164(typed: string): string | null {
  const t = typed.replace(/[\s()-]/g, '');
  if (/^\+\d{8,15}$/.test(t)) return t;
  if (/^0\d{9,10}$/.test(t)) return `+234${t.slice(1)}`;
  if (/^\d{10}$/.test(t)) return `+234${t}`;
  return null;
}

type Way = 'choose' | 'email' | 'phone';

/**
 * The text's 6 numbers in a box each, as the app has them (otp_screen.dart `_CodeInput`): one input underneath takes
 * typing, paste and the computer's or phone's autofill, and the last number signs in.
 */
function CodeBoxes({ value, disabled, onChange, onDone }: { value: string; disabled: boolean; onChange: (v: string) => void; onDone: (v: string) => void }) {
  const [focused, setFocused] = useState(true);
  const field = useRef<HTMLInputElement>(null);
  // Read-only while it checks, not disabled, so it keeps the cursor for typing again after a wrong code.
  useEffect(() => {
    if (!disabled) field.current?.focus();
  }, [disabled]);
  return (
    <label className="codeboxes">
      <input
        ref={field}
        inputMode="numeric"
        autoComplete="one-time-code"
        aria-label="The 6-number code"
        value={value}
        readOnly={disabled}
        autoFocus
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => {
          const v = e.target.value.replace(/\D/g, '').slice(0, 6);
          onChange(v);
          if (v.length === 6) onDone(v);
        }}
      />
      {Array.from({ length: 6 }, (_, i) => (
        <span key={i} className={`codebox mono${focused && !disabled && i === Math.min(value.length, 5) ? ' on' : ''}`} aria-hidden="true">
          {value[i] ?? ''}
        </span>
      ))}
    </label>
  );
}

export function SignIn() {
  const { elsewhere } = useStore(session);
  const [way, setWay] = useState<Way>('choose');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [sent, setSent] = useState<ConfirmationResult | null>(null);
  const captcha = useRef<RecaptchaVerifier | null>(null);

  useEffect(() => () => captcha.current?.clear(), []);

  const run = async (f: () => Promise<unknown>) => {
    setBusy(true);
    setError('');
    setNote('');
    try {
      await f();
    } catch (e) {
      setError(words(codeOf(e)));
    } finally {
      setBusy(false);
    }
  };

  const google = () => run(() => signInWithPopup(auth, new GoogleAuthProvider()));
  const withEmail = () => {
    if (!email.trim()) return setError('Enter your email.');
    if (!password) return setError('Enter your password.');
    run(() => signInWithEmailAndPassword(auth, email.trim(), password));
  };
  const forgot = () => {
    if (!email.trim()) return setError('Enter your email above, then choose Forgot password.');
    // Firebase answers the same whether or not there's an account, so nobody can find out who has one.
    run(async () => {
      await sendPasswordResetEmail(auth, email.trim());
      setNote(`If there’s an account for ${email.trim()}, an email is on its way with a link to set a new password.`);
    });
  };
  const textMe = () => {
    const number = phoneE164(phone);
    if (!number) return setError('That number doesn’t look right. Start it with + and the country code.');
    run(async () => {
      if (usingEmulators) auth.settings.appVerificationDisabledForTesting = true;
      captcha.current ??= new RecaptchaVerifier(auth, 'recaptcha', { size: 'invisible' });
      setSent(await signInWithPhoneNumber(auth, number, captcha.current));
      setNote(`A code is on its way to ${number}.`);
    });
  };
  const confirm = (typed = code) => {
    if (!sent) return;
    if (!/^\d{6}$/.test(typed.trim())) return setError('The code is 6 numbers.');
    run(async () => {
      try {
        await sent.confirm(typed.trim());
      } catch (e) {
        // Empty boxes to type it again, rather than six numbers to take back one by one.
        setCode('');
        throw e;
      }
    });
  };

  return (
    <div className="gate">
      <div className="gate-card">
        <div className="gate-brand">
          <Mark className="gate-mark" />
          <span className="title">Routr Dispatch</span>
        </div>
        <p className="ink2" style={{ margin: '6px 0 22px' }}>
          Your business’s day from your computer. Sign in with the account you use in the Routr app, or a new one to create your business here.
        </p>
        {elsewhere && <div className="gate-warn">This browser was signed out from another device on your account.</div>}
        {missingConfig.length > 0 && (
          <div className="gate-warn">
            This copy of Routr Dispatch isn’t connected to Firebase yet: {missingConfig.join(', ')} {missingConfig.length === 1 ? 'is' : 'are'} missing
            (control-room/README.md).
          </div>
        )}
        {way === 'choose' && (
          <div className="gate-ways">
            <button className="btn strong big" onClick={google} disabled={busy}>
              <GoogleG />
              Continue with Google
            </button>
            <button className="btn big" onClick={() => setWay('email')} disabled={busy}>
              <Icon name="envelopeSimple" />
              Sign in with email
            </button>
            <button className="btn big" onClick={() => setWay('phone')} disabled={busy}>
              <Icon name="phone" />
              Text me a code
            </button>
          </div>
        )}
        {way === 'email' && (
          <form
            className="gate-form"
            onSubmit={(e) => {
              e.preventDefault();
              withEmail();
            }}
          >
            <label className="field big">
              <Icon name="envelopeSimple" />
              <input type="email" autoComplete="username" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus spellCheck={false} />
            </label>
            <label className="field big">
              <Icon name="lockSimple" />
              <input type="password" autoComplete="current-password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
            </label>
            <button className="btn primary big" type="submit" disabled={busy}>
              Sign in
            </button>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <button type="button" className="link" onClick={forgot} disabled={busy}>
                Forgot password?
              </button>
              <button type="button" className="link ink2" onClick={() => setWay('choose')}>
                Other ways
              </button>
            </div>
          </form>
        )}
        {way === 'phone' && (
          <form
            className="gate-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (sent) confirm();
              else textMe();
            }}
          >
            {!sent ? (
              <label className="field big">
                <Icon name="phone" />
                <input type="tel" autoComplete="tel" placeholder="+234 803 000 0000" value={phone} onChange={(e) => setPhone(e.target.value)} autoFocus />
              </label>
            ) : (
              <CodeBoxes
                value={code}
                disabled={busy}
                onChange={(v) => {
                  setCode(v);
                  setError('');
                }}
                onDone={(v) => confirm(v)}
              />
            )}
            <button className="btn primary big" type="submit" disabled={busy}>
              {sent ? 'Sign in' : 'Text me a code'}
            </button>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              {sent ? (
                <button type="button" className="link" onClick={() => { setSent(null); setCode(''); setNote(''); }}>
                  Use another number
                </button>
              ) : (
                <span className="cap">Standard text rates may apply.</span>
              )}
              <button type="button" className="link ink2" onClick={() => { setWay('choose'); setSent(null); }}>
                Other ways
              </button>
            </div>
          </form>
        )}
        <div id="recaptcha" />
        {error && (
          <div className="gate-error" role="alert">
            {error}
          </div>
        )}
        {note && (
          <div className="cap" role="status" style={{ marginTop: 12 }}>
            {note}
          </div>
        )}
        <div className="cap" style={{ marginTop: 26 }}>
          For business owners. Couriers use the Routr app. Signing in means you agree to Routr’s{' '}
          <a className="link" href="https://goroutr.com/terms">terms</a> and{' '}
          <a className="link" href="https://goroutr.com/privacy">privacy notice</a>.
        </div>
      </div>
    </div>
  );
}

/** Signed in with an account that isn't a business's owner: why, and the way out. */
export function NotOwner({ gate }: { gate: Extract<Gate, { state: 'notOwner' }> }) {
  const who = accountWords(gate.user);
  const why = {
    noAccount: `There’s no Routr account for ${who} yet.`,
    courier: `${who} is a courier’s account. Routr Dispatch is for business owners: set up a business in the Routr app, or sign in with the owner’s account.`,
    inBusiness: `${who} belongs to a courier in someone else’s business. Only the business’s owner can open Routr Dispatch.`,
    unreadable: `Routr couldn’t read the account for ${who}. Check your connection and try again.`,
  }[gate.why];
  return (
    <div className="gate">
      <div className="gate-card">
        <Mark className="gate-mark" />
        <div className="title" style={{ fontSize: 26, marginTop: 18 }}>
          This is for business owners
        </div>
        <p className="ink2" style={{ margin: '8px 0 22px' }}>
          {why}
        </p>
        <button className="btn strong big" onClick={() => signOut()}>
          <Icon name="signOut" />
          Sign out
        </button>
      </div>
    </div>
  );
}

/** Google's "G", in Google's colours, as its sign-in buttons carry it. */
function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}
